/**
 * ✅ Inizializzazione e gestione Facebook SDK v18.0
 * Questo modulo gestisce il caricamento e l'inizializzazione del Facebook SDK
 * per abilitare le funzionalità di condivisione social
 * 
 * @requires @types/facebook-js-sdk - Installare con: npm install --save-dev @types/facebook-js-sdk
 * @requires FACEBOOK_APPID - Dichiarato in globals.ts
 */

/**
 * ✅ Inizializza Facebook SDK quando è pronto
 */
window.fbAsyncInit = function (): void {
    // Verifica che l'App ID sia definito
    if (typeof window.FACEBOOK_APPID === 'undefined') {
        console.error('❌ FACEBOOK_APPID non definito. Aggiungi <script>var FACEBOOK_APPID = "TUO_APP_ID";</script> prima di questo script.');
        return;
    }

    // Inizializza Facebook SDK
    window.FB.init({
        appId: window.FACEBOOK_APPID,
        cookie: true,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v18.0' // ✅ Versione aggiornata (ultima stabile)
    });

    // Log page view per analytics
    window.FB.AppEvents.logPageView();

    // ✅ Dispatch evento custom per notificare che FB SDK è pronto
    window.dispatchEvent(new Event('fb-sdk-ready'));
    
    console.log('✅ Facebook SDK inizializzato correttamente');
};

/**
 * ✅ Carica dinamicamente lo script Facebook SDK
 */
(function loadFacebookSDK(d: Document, s: string, id: string): void {
    const fjs = d.getElementsByTagName(s)[0];
    
    // Previeni caricamento duplicato
    if (d.getElementById(id)) {
        console.warn('⚠️ Facebook SDK già caricato');
        return;
    }

    // Crea elemento script
    const js = d.createElement(s) as HTMLScriptElement;
    js.id = id;
    js.src = "https://connect.facebook.net/it_IT/sdk.js"; // ✅ Versione italiana
    js.async = true;
    js.defer = true;

    // Gestione errori di caricamento
    js.onerror = function(): void {
        console.error('❌ Errore durante il caricamento di Facebook SDK');
        // Dispatch evento di errore
        window.dispatchEvent(new Event('fb-sdk-error'));
    };

    // Inserisci lo script nel DOM
    if (fjs && fjs.parentNode) {
        fjs.parentNode.insertBefore(js, fjs);
    } else {
        d.head.appendChild(js);
    }
    
    console.log('📡 Caricamento Facebook SDK in corso...');
})(document, 'script', 'facebook-jssdk');

/**
 * ✅ Helper per attendere che Facebook SDK sia pronto
 * @param timeoutMs Timeout massimo in millisecondi (default: 5000)
 * @returns Promise che risolve a true se FB è caricato, false se timeout
 * 
 * @example
 * ```typescript
 * import { waitForFacebookSDK } from '~/socialFacebook';
 * 
 * const isReady = await waitForFacebookSDK();
 * if (isReady) {
 *     window.FB.ui({ method: 'share', href: window.location.href });
 * }
 * ```
 */
export async function waitForFacebookSDK(timeoutMs: number = 5000): Promise<boolean> {
    // ✅ Se FB è già disponibile, risolvi subito
    if (typeof window.FB !== 'undefined' && window.FB !== null) {
        return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
        let timeoutId: number;
        
        // ✅ Handler per evento ready
        const onReady = (): void => {
            clearTimeout(timeoutId);
            window.removeEventListener('fb-sdk-error', onError);
            resolve(true);
        };
        
        // ✅ Handler per evento error
        const onError = (): void => {
            clearTimeout(timeoutId);
            window.removeEventListener('fb-sdk-ready', onReady);
            resolve(false);
        };

        // ✅ Timeout
        timeoutId = window.setTimeout(() => {
            window.removeEventListener('fb-sdk-ready', onReady);
            window.removeEventListener('fb-sdk-error', onError);
            console.warn('⚠️ Timeout durante l\'attesa di Facebook SDK');
            resolve(false);
        }, timeoutMs);

        // ✅ Listener per eventi
        window.addEventListener('fb-sdk-ready', onReady, { once: true });
        window.addEventListener('fb-sdk-error', onError, { once: true });
    });
}

/**
 * ✅ Verifica se Facebook SDK è disponibile
 * @returns true se FB SDK è caricato e pronto
 */
export function isFacebookSDKReady(): boolean {
    return typeof window.FB !== 'undefined' && window.FB !== null;
}

/**
 * ✅ Condivide un URL su Facebook
 * @param url URL da condividere (default: pagina corrente)
 * @param options Opzioni aggiuntive per la condivisione
 * @returns Promise che risolve con la risposta del dialog
 * 
 * @example
 * ```typescript
 * import { shareOnFacebook } from '~/socialFacebook';
 * 
 * try {
 *     const result = await shareOnFacebook(window.location.href, {
 *         quote: 'Guarda questa vendita!',
 *         hashtag: '#reteaste'
 *     });
 *     console.log('Condiviso!', result);
 * } catch (error) {
 *     console.error('Errore condivisione:', error);
 * }
 * ```
 */
export async function shareOnFacebook(
    url: string = window.location.href,
    options?: { quote?: string; hashtag?: string }
): Promise<facebook.ShareDialogResponse> {
    // ✅ Attendi che FB SDK sia pronto
    const isReady = await waitForFacebookSDK();

    if (!isReady) {
        throw new Error('Facebook SDK non disponibile');
    }

    return new Promise<facebook.ShareDialogResponse>((resolve, reject) => {
        try {
            window.FB.ui(
                {
                    method: 'share',
                    href: url,
                    quote: options?.quote,
                    hashtag: options?.hashtag
                } as facebook.ShareDialogParams,
                (response: facebook.ShareDialogResponse) => {
                    if (response && response.error_message) {
                        reject(new Error(response.error_message));
                    } else {
                        resolve(response);
                    }
                }
            );
        } catch (error) {
            reject(error);
        }
    });
}

// ✅ Export per compatibilità con moduli ES6
export default {
    waitForFacebookSDK,
    isFacebookSDKReady,
    shareOnFacebook
};
